import { Component, computed, OnDestroy, OnInit, signal, Signal, WritableSignal, inject } from '@angular/core';
import { LoaderComponent } from '../../../../../shared/components/loader/loader.component';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  AccountingAccount,
  AccountingConnectionStatus,
  AccountingProviderCompany,
} from '../../../../../_interfaces/accounting.interface';
import { AccountingService } from '../../_services/accounting/accounting.service';
import { AccountingApiService } from '../../../../../_services/accounting-service/accounting-api/accounting-api.service';
import { NgSelectComponent } from '@ng-select/ng-select';
import { BoFormsModule } from '@bo/ng-forms';
import { Store } from '@ngxs/store';
import { AccountingSelectors } from '../../../../../store/accounting/accounting.selectors';
import { toSignal } from '@angular/core/rxjs-interop';
import { updateParamsObject } from '@bo/ng-utils';
import { HttpParams } from '@angular/common/http';
import { SetAccountingProviderStateFromSelectingSourceAccount } from '../../../../../store/accounting/accounting.actions';

@Component({
  selector: 'app-source-account',
  imports: [LoaderComponent, NgSelectComponent, BoFormsModule, RouterLink],
  templateUrl: './source-account.component.html',
})
export class SourceAccountComponent implements OnInit, OnDestroy {
  readonly accountingService = inject(AccountingService);
  readonly accountingApiService = inject(AccountingApiService);
  readonly router = inject(Router);
  readonly route = inject(ActivatedRoute);
  readonly store = inject(Store);

  readonly sourceAccount$ = this.store.select(AccountingSelectors.sourceAccount);
  readonly existingSourceAccount = toSignal(this.sourceAccount$, { initialValue: undefined });
  protected currentExistingSourceAccount: boolean = false;
  private readonly subscriptions = new Subscription();
  protected readonly AccountingProviderCompany = AccountingProviderCompany;
  protected accountingProvider: Signal<AccountingProviderCompany | undefined> = computed(() =>
    this.accountingService.provider(),
  );

  protected newBankAccountsCreated: Signal<boolean | undefined> = computed(() => {
    return this.accountingService.providerNewBankAccountsCreated();
  });

  protected connectionStatus: Signal<AccountingConnectionStatus | undefined> = computed(() => {
    return this.accountingService.connectionStatus();
  });

  protected organisationNotSelected: Signal<boolean> = computed(
    () => this.connectionStatus() === AccountingConnectionStatus.HasTenants,
  );

  protected sourceAccOptions: AccountingAccount[] = [];
  protected selectedAccount: number | undefined;
  protected showError: string | undefined;

  protected queryParams = new HttpParams();

  loading: WritableSignal<boolean> = signal(true);
  failed: WritableSignal<boolean> = signal(false);
  hasSourceAccountOptions: WritableSignal<boolean> = signal(false);

  providerImgUrl = computed(() => {
    if (this.accountingProvider() === AccountingProviderCompany.XERO) {
      return './assets/accounting-logos/contra-accounts/xero.svg';
    }
    if (this.accountingProvider() === AccountingProviderCompany.QUICKBOOKS) {
      return './assets/accounting-logos/contra-accounts/qb.svg';
    }
    if (this.accountingProvider() === AccountingProviderCompany.SAGE) {
      return './assets/accounting-logos/contra-accounts/sage.svg';
    } else {
      return '';
    }
  });

  protected hasStatusError: Signal<boolean> = computed(() => {
    return (
      this.accountingService.connectionStatus() === AccountingConnectionStatus.Error ||
      this.accountingService.connectionStatus() === AccountingConnectionStatus.HttpError ||
      this.accountingService.connectionStatus() === AccountingConnectionStatus.AuthError
    );
  });

  ngOnInit(): void {
    this.subscriptions.add(
      this.accountingApiService.getAccountConnectionDetails().subscribe({
        next: (res) => {
          this.selectedAccount = res.source_account?.id;
          this.currentExistingSourceAccount = Boolean(res.source_account);
        },
      }),
    );
    this.subscriptions.add(
      this.accountingApiService.getAccountingSourceAccount().subscribe({
        next: (res) => {
          this.hasSourceAccountOptions.set(res.length > 0);
          this.sourceAccOptions = res;
        },
        error: () => {
          this.failed.set(true);
        },
        complete: () => {
          this.loading.set(false);
        },
      }),
    );
  }

  onSave() {
    if (this.selectedAccount) {
      this.subscriptions.add(
        this.accountingApiService.updateSourceAccount(this.selectedAccount).subscribe({
          next: () => {
            // Wait for the state to update before redirecting
            this.subscriptions.add(
              this.store.dispatch(new SetAccountingProviderStateFromSelectingSourceAccount()).subscribe({
                next: () => {
                  // brand-new connection
                  if (this.newBankAccountsCreated()) {
                    if (this.currentExistingSourceAccount) {
                      this.queryParams = this.queryParams.set('update', 'source-account');
                      this.router
                        .navigate(['/accounting/integrations'], {
                          queryParams: updateParamsObject(this.queryParams),
                        })
                        .then(() => {});
                    } else {
                      // Success screen showing the newly created accounts.
                      this.router.navigate(['../contra-accounts'], { relativeTo: this.route });
                    }
                  } else if (this.currentExistingSourceAccount) {
                    this.queryParams = this.queryParams.set('update', 'source-account');
                    this.router
                      .navigate(['/accounting/integrations'], {
                        queryParams: updateParamsObject(this.queryParams),
                      })
                      .then(() => {});
                  } else {
                    // this is a "reconnect" from a previous integration (bank accounts already exist on provider)
                    // just redirect, no need to display more details
                    this.queryParams = this.queryParams.set('reconnect', 'complete');
                    this.router
                      .navigate(['/accounting/integrations'], {
                        queryParams: updateParamsObject(this.queryParams),
                      })
                      .then(() => {});
                  }
                },
              }),
            );
          },
          error: (error) => {
            this.showError = error.error.non_field_errors[0];
          },
        }),
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
